import React, { useState } from 'react';
import { Divider, Button, Row, Col } from 'antd';
import { isNil } from 'lodash';
import moment from 'moment';

import {
  FullBOUserResponseModel,
  BOUserTypeEnum,
} from 'api/backofficeApiClient';
import PricingAgreementModal, {
  PricingAgreementType,
} from 'components/modals/PricingAgreementModal';
import BaseAlert from 'components/wrappers/BaseAlert';
import BasePageCard from 'components/wrappers/BasePageCard';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import { LoadRecruiterDetailsOptions } from 'features/recruiter/RecruiterDetailsPage/types';
import { getFundingTypeLabel } from 'utils/clientUtils';

import style from './PricingAgreementSection.module.less';

type Props = {
  recruiterDetails: FullBOUserResponseModel;
  loadRecruiterDetails: (
    options?: LoadRecruiterDetailsOptions,
  ) => Promise<void>;
};

export default function PricingAgreementSection(props: Props) {
  const { recruiterDetails, loadRecruiterDetails } = props;
  const [isPricingAgreementModalOpen, setIsPricingAgreementModalOpen] =
    useState(false);
  const { boUserType, businessInfo } = recruiterDetails;

  if (!businessInfo) return null;

  const { priceAgreement } = businessInfo;

  const onAgreementSent = async () => {
    await loadRecruiterDetails();
    BaseAlert.success('Pricing changes saved.');
  };

  return (
    <>
      {isPricingAgreementModalOpen && (
        <PricingAgreementModal
          agreementType={PricingAgreementType.AdminEdit}
          recruiter={recruiterDetails}
          onAgreementSent={onAgreementSent}
          onClose={() => setIsPricingAgreementModalOpen(false)}
        />
      )}

      <BasePageCard
        title="Pricing agreement"
        className="stretch-card"
        icons={<span className="icon icon-pricing-agreement"></span>}
        subTitle={
          !!priceAgreement && (
            <>
              {priceAgreement?.hasChangeRequest && (
                <div className={style.priceWrapper}>
                  <span className="icon icon-attention-filled" />
                  <span>Changes requested, please respond</span>
                </div>
              )}

              <Row gutter={[4, 4]} justify="end">
                <Col>
                  <Button
                    onClick={() => setIsPricingAgreementModalOpen(true)}
                    icon={<span className="icon icon-edit" />}
                    className="with-icon mobile-icon-only"
                    size="large"
                  >
                    Edit
                  </Button>
                </Col>
              </Row>
            </>
          )
        }
      >
        {!priceAgreement && (
          <div className="content-wrapper">
            <BaseSectionMessage
              title=""
              type="info"
              message={
                <>
                  {boUserType === BOUserTypeEnum.Company && (
                    <span>
                      Waiting for administrator to respond. Please send a
                      pricing agreement as soon as possible.
                    </span>
                  )}

                  {boUserType === BOUserTypeEnum.SelfEmployed && (
                    <span>
                      Waiting for administrator to review and respond. If there
                      are pending items, please provide them to us to expedite
                      the process.
                    </span>
                  )}

                  {boUserType === BOUserTypeEnum.International && (
                    <span>
                      Waiting for administrator to review and respond. If there
                      are pending items, please provide them to expedite the
                      process.
                    </span>
                  )}
                </>
              }
            />
          </div>
        )}

        {!!priceAgreement && (
          <>
            <div className="content-wrapper">
              <Row gutter={[4, 4]} className="titled-sheet">
                {priceAgreement.w2InfoList?.map((set, index) => (
                  <React.Fragment
                    key={`price-agreement-${set.w2Price}-${index}`}
                  >
                    <Col xs={24} md={12}>
                      <div className="title">W2 price:</div>
                      <div>{set.w2Price?.toFixed(2)}%</div>
                    </Col>

                    <Col xs={24} md={12}>
                      <div className="title">W2 states:</div>
                      <div>
                        {set.placementStates
                          ?.map((state) => state.name)
                          .join(', ')}
                      </div>
                    </Col>

                    {(set.workCodes || [])?.length > 0 && (
                      <Col xs={24} md={12}>
                        <div className="title">WC Codes:</div>
                        <div>
                          {set.workCodes?.map((code) => code).join(', ')}
                        </div>
                      </Col>
                    )}
                  </React.Fragment>
                ))}

                {!isNil(priceAgreement.icPrice) && (
                  <Col xs={24} md={12}>
                    <div className="title">IC price:</div>
                    <div>{priceAgreement.icPrice.toFixed(2)}%</div>
                  </Col>
                )}
              </Row>
            </div>

            {!isNil(priceAgreement.fundingType) && (
              <>
                <Divider className="no-margin" />

                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    <Col xs={24} md={12}>
                      <div className="title">Funding type:</div>
                      <div>
                        {getFundingTypeLabel(priceAgreement.fundingType)}
                      </div>
                    </Col>
                  </Row>
                </div>
              </>
            )}

            {priceAgreement.isConfirmation && (
              <>
                <Divider className="no-margin" />

                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    <Col xs={24} md={12}>
                      <div className="title">Customer signature:</div>
                      <div>{priceAgreement.confirmationSignature}</div>
                    </Col>

                    <Col xs={24} md={12}>
                      <div className="title">Date signed:</div>
                      <div>
                        {moment(priceAgreement.confirmationDate).format(
                          'MM/DD/YYYY',
                        )}
                      </div>
                    </Col>
                  </Row>

                  <div className={style.note}>
                    <span className="icon icon-check-outlined" />
                    <span>Pricing agreement confirmed.</span>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </BasePageCard>
    </>
  );
}
